import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, ArrowRight, Shield, CheckCircle, Sparkles } from 'lucide-react';
import logo from '../logo/logo.png';

export default function Login() {
  const navigate = useNavigate();
  const { login, checkAuthStatus } = useAuth();
  const [form, setForm] = useState({
    identifier: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setForm({ ...form, password: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const success = await login(form.identifier, form.password);
      if (success) {
        navigate('/dashboard', { replace: true });
        setTimeout(() => {
          checkAuthStatus();
        }, 200);
      }
    } catch (err) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 relative overflow-hidden">
      {/* Background Orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-brand-500/10 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-primary-500/10 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      <div className="relative z-10 w-full max-w-md mx-auto px-6 py-12">
        {/* Header */}
        <div className="flex flex-col items-center mb-10 text-center animate-fade-in">
          <div className="h-20 w-20 bg-brand-600 rounded-3xl flex items-center justify-center shadow-2xl shadow-brand-500/40 mb-8 transform hover:rotate-6 transition-transform duration-500">
            <img src={logo} alt="Logo" className="h-12 w-12 object-contain brightness-0 invert" />
          </div>
          <h1 className="text-4xl font-extrabold text-slate-900 dark:text-white mb-3 tracking-tight">
            Welcome <span className="gradient-text">Back</span>
          </h1>
          <p className="text-slate-500 dark:text-slate-400 font-medium">
            Manage your email ecosystem with precision
          </p>
        </div>

        {/* Card */}
        <div className="glass p-8 rounded-[2rem] shadow-2xl animate-slide-up border border-white/40 dark:border-slate-800/50">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800/30 p-4 rounded-2xl animate-shake">
                <p className="text-danger-700 dark:text-danger-400 font-semibold text-sm flex items-center gap-2">
                  <XCircle className="h-4 w-4" />
                  {error}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 dark:text-slate-300 ml-1">
                Access ID
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors group-focus-within:text-brand-500">
                  <Mail className="h-5 w-5 text-slate-400 group-hover:text-brand-500 transition-colors" />
                </div>
                <input
                  name="identifier"
                  value={form.identifier}
                  onChange={handleChange}
                  required
                  placeholder="Username or email"
                  className="input pl-12 h-14 bg-white/50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus:ring-brand-500/10 focus:border-brand-500"
                  autoFocus
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 dark:text-slate-300 ml-1">
                Security Key
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors group-focus-within:text-brand-500">
                  <Lock className="h-5 w-5 text-slate-400 group-hover:text-brand-500 transition-colors" />
                </div>
                <input
                  name="password"
                  onChange={handlePasswordChange}
                  required
                  placeholder="••••••••"
                  type="password"
                  className="input pl-12 h-14 bg-white/50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus:ring-brand-500/10 focus:border-brand-500"
                  autoComplete="current-password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full h-14 text-lg shadow-lg hover:shadow-brand-500/30 group active:scale-[0.98] transition-all"
            >
              {loading ? (
                <RefreshCw className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  Enter Dashboard
                  <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-slate-100 dark:border-slate-800/60 flex items-center justify-center gap-6">
            <div className="flex flex-col items-center gap-1 opacity-60">
              <Shield className="h-5 w-5 text-brand-600" />
              <span className="text-[10px] uppercase font-bold tracking-tighter">Secure</span>
            </div>
            <div className="flex flex-col items-center gap-1 opacity-60">
              <Sparkles className="h-5 w-5 text-brand-600" />
              <span className="text-[10px] uppercase font-bold tracking-tighter">Fast</span>
            </div>
            <div className="flex flex-col items-center gap-1 opacity-60">
              <CheckCircle className="h-5 w-5 text-brand-600" />
              <span className="text-[10px] uppercase font-bold tracking-tighter">Verified</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-10 text-center space-y-4">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
            Powered by <span className="text-brand-600 dark:text-brand-400">UnCutSolutions</span>
          </p>
        </div>
      </div>
    </div>
  );
}